import {
  type ComponentType,
  createElement,
  type ReactNode,
  Suspense,
  useState,
} from "react";

export type LazyComponent<Props extends object> = ComponentType<Props> & {
  /** Loads the code now; once it has, the component renders at once, without suspending. */
  preload: () => Promise<unknown>;
};

/** One download of the code: a failed one is replaced by the next mount (a reset, a remount). */
type Attempt<Props extends object> = {
  loading: Promise<ComponentType<Props>>;
  /** Settles when `loading` does and never rejects: what a render suspends on. */
  settled: Promise<void>;
  failure: { error: unknown } | null;
};

type LazyComponentOptions = {
  /** What draws while the code downloads; nothing by default. */
  fallback?: ReactNode;
};

/**
 * A component whose code loads the first time it renders, and draws nothing until it has
 * (NEXT.md A2): for the views a registry lists by name, so listing them does not put their code
 * (and their editors, charts, grids) in the page's first load. Each carries its own Suspense, so
 * a caller renders it as any component. `load` resolves to the component, e.g.
 * `lazyComponent(() => import("./components/ReadViews").then((module) => module.MediaListView))`.
 *
 * - The element tree is the same before and after the code arrives (Suspense › Slot › View), so
 *   a parent's re-render after the load never remounts the view and its state survives.
 * - A failed download throws to the nearest error boundary. It is not cached: the next mount
 *   (the boundary's reset, a reopened panel) and the next `preload()` download again, so a
 *   "Retry" works without reloading the page.
 */
export const lazyComponent = <Props extends object>(
  load: () => Promise<ComponentType<Props>>,
  { fallback = null }: LazyComponentOptions = {}
): LazyComponent<Props> => {
  let loaded: ComponentType<Props> | null = null;
  let current: Attempt<Props> | null = null;

  const attempt = (): Attempt<Props> => {
    if (current !== null && current.failure === null) return current;
    const loading = load();
    const next: Attempt<Props> = {
      loading,
      settled: Promise.resolve(),
      failure: null,
    };
    next.settled = loading.then(
      (View) => {
        loaded = View;
      },
      (error: unknown) => {
        next.failure = { error };
      }
    );
    current = next;
    return next;
  };

  // Throws the pending download (Suspense waits on it) or its failure (the error boundary shows
  // it) until the code is here, then draws the view: the same component at the same place.
  const Slot = ({
    mounted,
    viewProps,
  }: {
    mounted: Attempt<Props>;
    viewProps: Props;
  }) => {
    if (loaded !== null) return createElement(loaded, viewProps);
    if (mounted.failure !== null) throw mounted.failure.error;
    throw mounted.settled;
  };

  const Loaded = (props: Props) => {
    // The attempt this mount waits on, kept across Suspense's re-render after a failure so the
    // failure reaches the boundary instead of starting another download at once.
    const [mounted] = useState(attempt);
    return (
      <Suspense fallback={fallback}>
        <Slot mounted={mounted} viewProps={props} />
      </Suspense>
    );
  };
  return Object.assign(Loaded, { preload: () => attempt().loading });
};

const isLazyComponent = (
  value: unknown
): value is { preload: () => Promise<unknown> } =>
  typeof value === "function" &&
  "preload" in value &&
  typeof value.preload === "function";

/** Loads every lazy component of a registry (views by name), e.g. before a test renders them. */
export const preloadComponents = async (
  registry: Readonly<Record<string, unknown>>
) => {
  await Promise.all(
    Object.values(registry)
      .filter(isLazyComponent)
      .map((component) => component.preload())
  );
};
